import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { Plus, UsersRound } from "lucide-react";

import RoleItem from "./RoleItem";
import CapacityBanner from "./CapacityBanner";
import {
  getEventCapacity,
  getEventRoles,
  getUpgradeRequest,
  isUpgradeableEvent,
} from "./crewSizeUpgradeUtils";
import { PAYMENT_REQUEST_STATUSES } from "../BasicInfo/paymentStateUtils";
import CrewSizeUpgradeFlow from "../CrewSizeUpgradeFlow";
import CancelUpgradeRequestModal from "../Modals/CancelUpgradeRequestModal";
import { UIEmptyState } from "../../../ui";
import messages from "../../../../assets/locale/messages";
import { formatDate } from "../../../../utils/Helpers";
import {
  fillTemplate,
  formatAmount,
} from "../../Form/Steps/ChooseCrewSize/crewSizeUtils";

import "../Zones/EmptyZonesState.scss";

const EventRoles = ({
  event,
  portalVariant,
  onAddRole,
  showCapacityBanner,
  canUpgradeCrewSize,
  onRefreshEvent,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang];
  const t = events.viewEvents;
  const upgradeT = t.crewSizeUpgrade;

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const roles = getEventRoles(event);
  const capacity = showCapacityBanner ? getEventCapacity(event) : null;
  // One request at a time: while one is pending, no other upgrade can be raised.
  const upgradeRequest = getUpgradeRequest(event);
  const hasPendingUpgrade =
    upgradeRequest?.status === PAYMENT_REQUEST_STATUSES.pending;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {capacity && (
        <CapacityBanner
          t={upgradeT.banner}
          used={capacity.used}
          ceiling={capacity.ceiling}
          sizeLabel={
            capacity.crewSize.name ??
            events.creatEvent.crewSize.sizes[capacity.crewSize.key]
          }
          cancelRequestLabel={t.crewSizeTab.upgradePending.cancel}
          pendingMessage={
            hasPendingUpgrade
              ? fillTemplate(upgradeT.banner.pending, {
                  size: upgradeRequest.crew_size_plan?.name ?? "",
                  amount: `${formatAmount(Number(upgradeRequest.total_amount ?? 0))} ${
                    events.creatEvent.crewSize.currency
                  }`,
                  // Figma: 22-03-2026 in both languages, like the Crew Size tab.
                  date: upgradeRequest.created_at
                    ? formatDate(upgradeRequest.created_at, "DD-MM-YYYY")
                    : "",
                })
              : undefined
          }
          onCancelRequest={
            canUpgradeCrewSize && hasPendingUpgrade
              ? () => setCancelOpen(true)
              : undefined
          }
          onUpgrade={
            canUpgradeCrewSize &&
            // A custom plan is changed by the Super Admin only.
            !capacity.crewSize.custom &&
            isUpgradeableEvent(event) &&
            !hasPendingUpgrade
              ? () => setUpgradeOpen(true)
              : undefined
          }
        />
      )}

      {roles.length ? (
        roles.map((role, idx) => (
          <RoleItem
            key={role?.id ?? role?.role_id ?? idx}
            role={role}
            defaultExpanded={idx === 0}
            portalVariant={portalVariant}
          />
        ))
      ) : (
        // Same empty state as the Zones tab.
        <UIEmptyState
          title={t.emptyRolesTitle}
          description={t.emptyRolesDescription}
          icon={<UsersRound size={40} strokeWidth={1.5} />}
          actionLabel={t.addNewRole}
          actionIcon={<Plus size={18} />}
          onAction={onAddRole}
          classNames={{
            description: "empty-zones-state__description",
            iconCircle: "empty-zones-state__icon-circle",
          }}
        />
      )}

      {upgradeOpen && (
        <CrewSizeUpgradeFlow
          event={event}
          currentCrewSize={capacity.crewSize}
          atCapacity={capacity.used >= capacity.ceiling}
          onClose={() => setUpgradeOpen(false)}
          onSubmitted={() => {
            setUpgradeOpen(false);
            onRefreshEvent?.({ silent: true });
          }}
        />
      )}
      {hasPendingUpgrade && (
        <CancelUpgradeRequestModal
          open={cancelOpen}
          requestId={upgradeRequest.id}
          onSettled={() => {
            setCancelOpen(false);
            onRefreshEvent?.({ silent: true });
          }}
        />
      )}

    </Box>
  );
};

EventRoles.propTypes = {
  event: PropTypes.object,
  portalVariant: PropTypes.oneOf(["company-portal", "client-portal"]),
  onAddRole: PropTypes.func,
  /** Company portal only: capacity banner above the roles of a published event. */
  showCapacityBanner: PropTypes.bool,
  canUpgradeCrewSize: PropTypes.bool,
  /** Reloads the event once an upgrade request is submitted or cancelled. */
  onRefreshEvent: PropTypes.func,
};

EventRoles.defaultProps = {
  event: null,
  portalVariant: "company-portal",
  onAddRole: undefined,
  showCapacityBanner: false,
  canUpgradeCrewSize: false,
  onRefreshEvent: undefined,
};

export default EventRoles;
